/**
 * Legal Case Tracker V2 — a focused list of cases that have escalated
 * past arrears (stage = bad_debt / written_off, or a `legal_stage` set).
 *
 * Mirrors the LoanAgeing list-page conventions:
 *   • KPI tiles across the top (total cases / total balance / by-stage)
 *   • Filter bar (handler, legal_stage, jurisdiction, search)
 *   • Compact table — click a row to open the case file
 *
 * Unlike the arrears page, this list is NOT scoped to today's CSV
 * report — legal cases stay here until ops mark them fully_paid /
 * written_off / back-to-collections.
 *
 * Phase 1 scope (this file):
 *   • Read-only list with filters + KPI tiles
 *   • Manual "Sync from Airtable" button (one-off import)
 *   • Row click → /legal/case/:case_id?source=legal (reuses CustomerFile)
 *
 * Phase 2 will add:
 *   • Legal Schedule centre-tab (DN expiry, SD2 hearing, etc.)
 *   • Legal Docs centre-tab
 *   • Stage edit dropdown wired to /cases/{id} PATCH
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { UnmatchedFoldersPanel } from "./UnmatchedFoldersPanel";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Scale, Search, RefreshCw, Loader2, AlertTriangle, ArrowRight,
  FileText, Filter, Calendar as CalIcon,
  Clock, X, Plus, Landmark, PauseCircle, FileSpreadsheet, CalendarX2,
  ArrowUp, ArrowDown, ArrowUpDown, MoreHorizontal, User as UserIcon, AlarmClock, Gavel, UserX,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import {
  listLegalCases, listLegalStages, getAgeingHandlers, sortNames,
  syncLegalCasesFromLoanDisk, getLegalLoanDiskSyncStatus,
  startBulkAirtableSync, getBulkAirtableSyncStatus, getLatestBulkAirtableSync,
  triggerOneDriveSync, getOneDriveSyncStatus, bulkAssignAgeingCases,
  getLegalAutoRefreshLatest, getLegalNotesBundle, getLegalProcessMe,
} from "@/lib/api";
import { handlerIdFor, rosterFrom } from "@/lib/handlerRoster";
import { toast } from "sonner";
import UnassignedLegalPanel from "./UnassignedLegalPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import FindLegalCaseDialog from "./FindLegalCaseDialog";
import { PriorityDot } from "./legalPriority";
import { useAccountStatuses } from "@/components/collections/StatusTags";

const fmtGBP = (n) =>
  `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

// Emma #1 — filters survive navigating into a case and back (per browser tab).
const LEGAL_FILTERS_SS_KEY = "legal-tracker-filters-v1";
const loadLegalSaved = () => {
  try { return JSON.parse(sessionStorage.getItem(LEGAL_FILTERS_SS_KEY) || "null") || {}; } catch { return {}; }
};

export default function LegalCaseTrackerV2() {
  const navigate = useNavigate();
  const [cases, setCases]               = useState([]);
  const [summary, setSummary]           = useState({});
  const [stages, setStages]             = useState([]);
  const [handlers, setHandlers]         = useState([]);          // Legal group — owner actions
  const [accountHandlers, setAccountHandlers] = useState([]);   // Collections group — account handler bulk assign
  const [roster, setRoster]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [legalMe, setLegalMe]           = useState(null);   // A1: is_legal_head → Unassigned panel
  useEffect(() => { getLegalProcessMe().then(setLegalMe).catch(() => setLegalMe({ is_legal_head: false })); }, []);

  // Filters — jurisdiction removed (item 13), actions_within_days added
  const [search, setSearch]                 = useState(() => loadLegalSaved().search || "");
  const [handlerFilter, setHandlerFilter]   = useState(() => loadLegalSaved().handlerFilter || "all");
  const [stageFilter, setStageFilter]       = useState(() => loadLegalSaved().stageFilter || "all");
  const [actionsWithinDays, setActionsWithinDays] = useState(() => loadLegalSaved().actionsWithinDays ?? null);  // null = inactive; integer = N days
  const [actionsInput, setActionsInput]     = useState(() => loadLegalSaved().actionsInput ?? 7);            // user-editable timeframe
  const [reviewOnly, setReviewOnly]         = useState(() => loadLegalSaved().reviewOnly || false);        // 'Review for Legal Proceedings' queue
  const [bsOnly, setBsOnly]                 = useState(() => loadLegalSaved().bsOnly || false);        // active Breathing Space (protected) cases only
  const [holdOnly, setHoldOnly]             = useState(() => loadLegalSaved().holdOnly || false);      // Dean 9 Oct 2026 — files kept on the Legal side by a legal hold
  const [noNextActionOnly, setNoNextActionOnly] = useState(() => loadLegalSaved().noNextActionOnly || false);  // Emma de31b7a3fd — nothing in the diary
  const [closedFilter, setClosedFilter]     = useState(() => loadLegalSaved().closedFilter || "live");       // live | include | only
  // Emma #6 — handling (in-house / out of jurisdiction / external) + jurisdiction
  const [handlingFilter, setHandlingFilter] = useState(() => loadLegalSaved().handlingFilter || "all");
  const [jurisdictionFilter, setJurisdictionFilter] = useState(() => loadLegalSaved().jurisdictionFilter || "all");
  // Emma #14 — manual status tag filter
  const [statusFilter, setStatusFilter] = useState(() => loadLegalSaved().statusFilter || "all");
  const accountStatuses = useAccountStatuses();
  // Dean, 8 Oct 2026 — quick views (client-side on the loaded set) + sort
  const { user: authUser } = useAuth();
  const myName = authUser?.name || "";
  const [quickView, setQuickView] = useState(() => loadLegalSaved().quickView || null);     // overdue | hearings_month | null
  const [sort, setSort] = useState(() => loadLegalSaved().sort || { key: "next_action", dir: "asc" });

  // Bulk handler assignment (item 12)
  const [selectedIds, setSelectedIds]   = useState(() => new Set());
  const [bulkBusy, setBulkBusy]         = useState(false);

  // Find Legal Case dialog (manual LoanDisk-search escalation)
  const [findOpen, setFindOpen]         = useState(false);
  const [bundleCase, setBundleCase]     = useState(null); // court-bundle viewer (Emma 7cb2874553)

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (handlerFilter !== "all")            params.handler             = handlerFilter;
      if (stageFilter !== "all")              params.legal_stage         = stageFilter;
      if (actionsWithinDays !== null)         params.actions_within_days = actionsWithinDays;
      if (reviewOnly)                         params.review_flag         = true;
      if (bsOnly)                             params.bs_protected        = true;
      if (holdOnly)                           params.legal_hold          = true;
      if (noNextActionOnly)                   params.no_next_action      = true;
      if (closedFilter === "include")         params.closed              = "include";
      if (closedFilter === "only")            params.closed              = "only";
      if (handlingFilter !== "all")           params.handling            = handlingFilter;
      if (jurisdictionFilter !== "all")       params.jurisdiction        = jurisdictionFilter;
      if (statusFilter !== "all")             params.manual_status       = statusFilter;
      if (search.trim())                      params.search              = search.trim();

      const r = await listLegalCases(params);
      setCases(r.cases || []);
      setSummary({
        total:           r.total_in_legal || 0,
        total_balance:   r.total_balance  || 0,
        by_stage:        r.by_stage       || {},
        by_handler:      r.by_handler     || {},
        handler_options: r.handler_options || [],
        review_count:    r.review_count   || 0,
        bs_count:        r.bs_protected_count || 0,
        hold_count:      r.legal_hold_count || 0,
        closed_count:    r.closed_count   || 0,
        reviews_due:     r.reviews_due_count || 0,
        no_next_action:  r.no_next_action_count || 0,
        live_count:      r.live_count || 0,
        history_count:   r.history_count || 0,
        live_breakdown:  r.live_breakdown || {},
      });
    } catch (e) {
      toast.error("Failed to load legal cases: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setLoading(false);
    }
  }, [handlerFilter, stageFilter, actionsWithinDays, reviewOnly, bsOnly, holdOnly, noNextActionOnly, closedFilter, handlingFilter, jurisdictionFilter, statusFilter, search]);

  // One-time refs — stages + handlers don't change between sessions
  // Dropdowns are alphabetical (Dean, 8 Oct 2026); handlers = Legal group for owner actions
  useEffect(() => {
    (async () => {
      try {
        const [s, h, ah] = await Promise.all([listLegalStages(), getAgeingHandlers("legal"), getAgeingHandlers("collections").catch(() => ({ handlers: [] }))]);
        setStages(s.stages || []);
        setHandlers(sortNames(h.handlers));
        setAccountHandlers(sortNames(ah.handlers));
        // one roster for both dropdowns: the bulk assign sends either group's name.
        // A person in both groups appears once, or their name would match twice and lose its id.
        const seen = new Set();
        setRoster([...rosterFrom(h), ...rosterFrom(ah)].filter((r) => {
          const key = `${r?.name}|${r?.user_id || ""}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        }));
      } catch (e) {
        // Non-fatal — case list still renders, just the dropdowns will be empty
        console.warn("Failed to load reference data:", e);
      }
    })();
  }, []);
  const stagesAlpha = useMemo(() => [...stages].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })), [stages]);
  const statusesAlpha = useMemo(() => [...accountStatuses].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })), [accountStatuses]);
  // Handler filter = people who actually hold a case in the current result set (legal owner or account handler)
  const filterHandlers = useMemo(() => sortNames(summary.handler_options || [], handlerFilter !== "all" && handlerFilter !== "Unassigned" ? handlerFilter : null), [summary.handler_options, handlerFilter]);

  // Re-fetch whenever filters change (debounced via the dependency array)
  useEffect(() => {
    const t = setTimeout(reload, 250);
    return () => clearTimeout(t);
  }, [reload]);

  // Map stage key → label for badge rendering
  const stageLabel = useMemo(() => {
    const m = {};
    for (const s of stages) m[s.key] = s.label;
    return m;
  }, [stages]);

  // ─── Unified "Refresh All" — orchestrates the two heavy sync jobs ──
  // Phase 1: LoanDisk legal-loans sync (1-3 min — paginates the whole loan
  //          book and upserts collections_cases for loan_status_id=3).
  // Phase 2: Bulk Airtable sync (~3 sec — pulls every CaseNotes blob and
  //          materialises timeline notes + deadlines + calendar events + P2Ps).
  //
  // Why front-end orchestration: both phases already have their own
  // background-task + polling infrastructure on the backend; chaining
  // them client-side keeps the backend simple and avoids a third
  // state-machine. The progress modal shows the current phase + per-phase
  // counters so ops can see both halves of the operation.
  const [refreshOpen, setRefreshOpen]       = useState(false);
  const [refreshRunning, setRefreshRunning] = useState(false);
  const [refreshPhase, setRefreshPhase]     = useState(null);   // 'loandisk' | 'airtable' | 'onedrive' | 'done' | 'failed'
  const [loanDiskState, setLoanDiskState]   = useState(null);   // the /sync-loandisk/status snapshot
  const [bulkSyncJob, setBulkSyncJob]       = useState(null);   // the /bulk-sync-airtable job snapshot
  const [oneDriveState, setOneDriveState]   = useState(null);   // the /sync-onedrive/status snapshot
  const [refreshError, setRefreshError]     = useState(null);

  // Recover any in-flight bulk Airtable job on mount (page-refresh-safe).
  // We don't auto-resume the LoanDisk phase because its singleton state
  // is in-memory only — a backend restart wipes it.
  useEffect(() => {
    (async () => {
      try {
        const r = await getLatestBulkAirtableSync();
        if (r?.job && r.job.status === "running") {
          setBulkSyncJob(r.job);
          setRefreshPhase("airtable");
          setRefreshRunning(true);
          setRefreshOpen(true);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  // Poll loop — interval picks the right endpoint based on the current
  // phase. Stops when the phase transitions to 'done' or 'failed'.
  useEffect(() => {
    if (!refreshRunning) return undefined;
    const timer = setInterval(async () => {
      try {
        if (refreshPhase === "loandisk") {
          const r = await getLegalLoanDiskSyncStatus();
          setLoanDiskState(r);
          if (r?.state === "completed") {
            // Phase 1 done — kick off phase 2.
            setRefreshPhase("airtable");
            try {
              const start = await startBulkAirtableSync();
              const jobId = start.job_id || start.already_running?.job_id;
              if (jobId) {
                const status = await getBulkAirtableSyncStatus(jobId);
                setBulkSyncJob(status.job);
              }
            } catch (e) {
              setRefreshError("Airtable phase failed to start: " + (e?.response?.data?.detail || e?.message));
              setRefreshPhase("failed");
              setRefreshRunning(false);
            }
          } else if (r?.state === "failed") {
            setRefreshError(r?.error || "LoanDisk sync failed");
            setRefreshPhase("failed");
            setRefreshRunning(false);
          }
        } else if (refreshPhase === "airtable" && bulkSyncJob?.job_id) {
          const r = await getBulkAirtableSyncStatus(bulkSyncJob.job_id);
          const job = r?.job;
          if (!job) return;
          setBulkSyncJob(job);
          if (job.status === "complete" || job.status === "failed") {
            if (job.status === "complete") {
              // Phase 2 done → kick off Phase 3 (OneDrive)
              setRefreshPhase("onedrive");
              try {
                await triggerOneDriveSync();
              } catch (e) {
                // Non-fatal — surface the error but still mark refresh done
                console.warn("OneDrive phase failed to start:", e);
                setRefreshPhase("done");
                setRefreshRunning(false);
                reload();
              }
            } else {
              setRefreshError(job.error || "Airtable bulk sync failed");
              setRefreshPhase("failed");
              setRefreshRunning(false);
            }
          }
        } else if (refreshPhase === "onedrive") {
          const r = await getOneDriveSyncStatus();
          setOneDriveState(r);
          if (r?.state === "completed" || r?.state === "failed") {
            setRefreshRunning(false);
            if (r.state === "completed") {
              const t = bulkSyncJob?.totals || {};
              const ldIm  = (loanDiskState?.imported || 0);
              const ldUp  = (loanDiskState?.updated  || 0);
              setRefreshPhase("done");
              toast.success(
                `Refresh complete — LoanDisk: ${ldIm} new, ${ldUp} updated. ` +
                `Airtable: ${t.imported_count || 0} notes, ${t.deadline_count || 0} deadlines, ` +
                `${t.calendar_events_created || 0} cal events, ${t.p2ps_created || 0} P2Ps, ` +
                `${t.cases_created || 0} new cases. ` +
                `OneDrive: ${r.matched} matched, ${r.unmatched} unmatched.`,
                { duration: 12000 },
              );
              reload();
            } else {
              setRefreshError((r.errors || []).join("; ") || "OneDrive sync failed");
              setRefreshPhase("failed");
            }
          }
        }
      } catch (e) {
        console.warn("refresh poll error:", e);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [refreshRunning, refreshPhase, bulkSyncJob?.job_id, loanDiskState?.imported, loanDiskState?.updated, reload]);

  const handleRefreshAll = async () => {
    setRefreshError(null);
    setLoanDiskState(null);
    setBulkSyncJob(null);
    setRefreshOpen(true);
    setRefreshPhase("loandisk");
    setRefreshRunning(true);
    try {
      // Kick off LoanDisk phase (fire-and-forget; the poll loop drives
      // the rest of the orchestration).
      await syncLegalCasesFromLoanDisk();
    } catch (e) {
      setRefreshError("LoanDisk sync failed to start: " + (e?.response?.data?.detail || e?.message));
      setRefreshPhase("failed");
      setRefreshRunning(false);
    }
  };

  // ─── Bulk handler assignment (item 12) — selection helpers live below `visibleCases` ──
  const toggleRow = (caseId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(caseId)) next.delete(caseId);
      else next.add(caseId);
      return next;
    });
  };
  // Reset selection whenever the visible set changes
  useEffect(() => { setSelectedIds(new Set()); }, [handlerFilter, stageFilter, actionsWithinDays, search]);

  // Emma #1 — persist the active filters for this browser tab
  useEffect(() => {
    try {
      sessionStorage.setItem(LEGAL_FILTERS_SS_KEY, JSON.stringify({
        search, handlerFilter, stageFilter, actionsWithinDays, actionsInput, reviewOnly, bsOnly, holdOnly, noNextActionOnly, closedFilter, handlingFilter, jurisdictionFilter, statusFilter, quickView, sort,
      }));
    } catch { /* ignore */ }
  }, [search, handlerFilter, stageFilter, actionsWithinDays, actionsInput, reviewOnly, bsOnly, holdOnly, noNextActionOnly, closedFilter, handlingFilter, jurisdictionFilter, statusFilter, quickView, sort]);

  // Dean, 8 Oct 2026 — quick views + sort are applied client-side on the loaded set (≤500 rows, instant)
  const monthKey = new Date().toISOString().slice(0, 7);
  const quickCounts = useMemo(() => ({
    overdue: cases.filter(c => (c.next_event?.days_overdue || 0) > 0).length,
    hearings_month: cases.filter(c => (c.hearing?.date || "").startsWith(monthKey)).length,
  }), [cases, monthKey]);
  const visibleCases = useMemo(() => {
    let rows = cases;
    if (quickView === "overdue") rows = rows.filter(c => (c.next_event?.days_overdue || 0) > 0);
    if (quickView === "hearings_month") rows = rows.filter(c => (c.hearing?.date || "").startsWith(monthKey));
    const dir = sort.dir === "desc" ? -1 : 1;
    const str = (v) => (v || "").toString().toLowerCase();
    const cmp = {
      customer:    (a, b) => str(a.customer_name).localeCompare(str(b.customer_name)),
      stage:       (a, b) => str(stageLabel[a.legal_stage] || a.legal_stage).localeCompare(str(stageLabel[b.legal_stage] || b.legal_stage)),
      next_action: (a, b) => (a.next_event?.date || "9999").localeCompare(b.next_event?.date || "9999"),
      latest_note: (a, b) => (b.latest_human_note?.at || "").localeCompare(a.latest_human_note?.at || ""),
      handler:     (a, b) => str(a.legal_handler || a.handler || "zzz").localeCompare(str(b.legal_handler || b.handler || "zzz")),
      account:     (a, b) => str(a.handler || "zzz").localeCompare(str(b.handler || "zzz")),
      legal_owner: (a, b) => str(a.legal_handler || a.handler || "zzz").localeCompare(str(b.legal_handler || b.handler || "zzz")),
      balance:     (a, b) => (a.balance_amount || 0) - (b.balance_amount || 0),
    }[sort.key] || (() => 0);
    const sorted = [...rows].sort((a, b) => cmp(a, b) * dir);
    if (sort.key === "next_action") {
      // files with nothing diarised sink to the bottom regardless of direction, biggest balance first
      const withEv = sorted.filter(c => c.next_event), without = sorted.filter(c => !c.next_event).sort((a, b) => (b.balance_amount || 0) - (a.balance_amount || 0));
      return [...withEv, ...without];
    }
    return sorted;
  }, [cases, quickView, sort, monthKey, stageLabel]);
  const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "balance" || key === "latest_note" ? "desc" : "asc" });
  const SortIcon = ({ k }) => sort.key !== k ? <ArrowUpDown className="w-3 h-3 opacity-40" /> : sort.dir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  const isMine = !!myName && handlerFilter === myName;
  const allSelected = visibleCases.length > 0 && visibleCases.every(c => selectedIds.has(c.case_id));
  const someSelected = visibleCases.some(c => selectedIds.has(c.case_id));
  const toggleAllVisible = () => setSelectedIds(allSelected ? new Set() : new Set(visibleCases.map(c => c.case_id)));

  // Emma #7 — last nightly auto-refresh status chip
  const [autoRefreshRun, setAutoRefreshRun] = useState(null);
  useEffect(() => {
    getLegalAutoRefreshLatest().then(r => setAutoRefreshRun((r.runs || [])[0] || null)).catch(() => {});
  }, []);

  // Emma #3/#10 — export the filtered tracker view as CSV
  const exportCsv = () => {
    const esc = (v) => { const s = String(v ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const head = ["Customer", "SF#", "Handler", "Legal stage", "Next action", "Next action due", "Days overdue",
                  "Latest note", "Balance £", "Hearing date", "Closed"];
    const rows = visibleCases.map(c => [
      c.customer_name || "", c.loan_application_id || "", c.legal_handler || c.handler || "Unassigned",
      (stages.find(s => s.key === c.legal_stage)?.label) || humanise(c.legal_stage) || "Not staged",
      c.next_event?.label || "", c.next_event?.date || "",
      c.next_event?.days_overdue > 0 ? c.next_event.days_overdue : "",
      c.latest_human_note?.text || "", c.balance_amount ?? "",
      c.hearing?.date || "", c.legal_closure?.type || "",
    ]);
    const csv = [head, ...rows].map(r => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `legal-case-tracker-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${rows.length} case${rows.length === 1 ? "" : "s"} to CSV`);
  };

  const handleBulkAssign = async (newHandler, field = "legal_handler") => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    try {
      const r = await bulkAssignAgeingCases({
        case_ids: ids,
        handler: newHandler,
        handler_user_id: handlerIdFor(roster, newHandler),
        field,
      });
      toast.success(
        `${field === "legal_handler" ? "Legal owner" : "Account handler"} → ${r.new_handler} on ${r.changed} case${r.changed === 1 ? "" : "s"}`,
      );
      setSelectedIds(new Set());
      reload();
    } catch (e) {
      toast.error("Bulk assign failed: " + (e?.response?.data?.detail || e?.message));
    } finally {
      setBulkBusy(false);
    }
  };

  // Tiny truncation helper for the latest-note column
  const truncate = (s, n) => {
    if (!s) return "";
    return s.length > n ? s.slice(0, n) + "…" : s;
  };

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <TooltipProvider delayDuration={150}>
    <div className="p-4 space-y-3 max-w-[1600px] mx-auto" data-testid="legal-case-tracker-v2">
      {/* Item 10: Compact header — title + inline KPI strip + actions cluster
          (mirrors LoanAgeing's "title · count · balance · upload" pattern). */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 min-w-0">
          <Scale className="w-5 h-5 text-red-700 shrink-0" />
          <h1 className="text-base font-semibold whitespace-nowrap">Legal Case Tracker</h1>
          {!loading && (
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0" data-testid="legal-summary-hero">
              <span className="text-slate-400">·</span>
              <span className="whitespace-nowrap" title={closedFilter === "live" ? `Live legal files — ${summary.live_breakdown?.legal || 0} Legal · ${summary.live_breakdown?.legal_ongoing || 0} open loan with legal ongoing · ${summary.live_breakdown?.legal_ld_open || 0} to tidy up (LD says Open). ${summary.history_count || 0} more in history.` : undefined}>
                <span className="font-semibold text-indigo-900 tabular-nums" data-testid="legal-kpi-total-cases">{summary.total || 0}</span>
                <span className="text-slate-500 ml-1">{closedFilter === "live" ? "live cases" : closedFilter === "only" ? "history" : "cases"}</span>
              </span>
              {closedFilter === "live" && (summary.live_breakdown?.legal_ld_open || 0) > 0 && (
                <span className="whitespace-nowrap">
                  <span className="font-semibold text-orange-700 tabular-nums" data-testid="legal-kpi-ld-open">{summary.live_breakdown.legal_ld_open}</span>
                  <span className="text-slate-500 ml-1">to tidy up</span>
                </span>
              )}
              <span className="whitespace-nowrap">
                <span className="font-semibold text-rose-700 tabular-nums" data-testid="legal-kpi-balance">{fmtGBP(summary.total_balance)}</span>
                <span className="text-slate-500 ml-1">at risk</span>
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-amber-800 tabular-nums" data-testid="legal-kpi-unassigned">{summary.by_handler?.Unassigned || 0}</span>
                <span className="text-slate-500 ml-1">no legal owner</span>
              </span>
              <span className="whitespace-nowrap">
                <span className="font-semibold text-indigo-900 tabular-nums" data-testid="legal-kpi-active">{countActiveStages(summary.by_stage)}</span>
                <span className="text-slate-500 ml-1">in active proceedings</span>
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={() => navigate("/legal/calendar")}
            data-testid="legal-open-calendar"
            title="Open the Legal Calendar — all DN/SD2/hearing dates across every case."
          >
            <CalIcon className="w-3.5 h-3.5 mr-1.5" />
            Legal Calendar
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs border-red-300 text-red-800 hover:bg-red-50"
            onClick={() => setFindOpen(true)}
            data-testid="legal-find-case"
            title="Search LoanDisk by SF number or customer name, then escalate that loan straight into the Legal Case Tracker (pushes loan_status_id=3 to LoanDisk)."
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Find Legal Case
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={exportCsv}
            disabled={cases.length === 0}
            title="Download the filtered tracker view as a CSV — case, handler, next action, latest note"
            data-testid="legal-export-csv"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
            Export CSV
          </Button>
          <Button
            size="sm"
            className="h-8 text-xs bg-red-700 hover:bg-red-800 text-white"
            onClick={handleRefreshAll}
            disabled={refreshRunning}
            data-testid="legal-refresh-all"
            title="Pull every legal case from LoanDisk, re-import all Airtable CaseNotes, AND sync OneDrive case-file metadata. Runs in three phases — LoanDisk (~1-3 min), Airtable (~3s), OneDrive (~30s). Idempotent — your portal edits survive."
          >
            {refreshRunning
              ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
            Refresh All
          </Button>
          {autoRefreshRun && (
            <span
              className={`text-[10px] px-1.5 py-0.5 rounded border ${autoRefreshRun.status === "success"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : autoRefreshRun.status === "running" ? "bg-blue-50 text-blue-800 border-blue-200"
                  : "bg-rose-50 text-rose-800 border-rose-200"}`}
              title={`Nightly auto-refresh (midnight London, 2am watchdog) — last run ${autoRefreshRun.trigger} on ${autoRefreshRun.date}: ${autoRefreshRun.status}`}
              data-testid="legal-auto-refresh-chip"
            >
              Auto {autoRefreshRun.date?.slice(5)} {autoRefreshRun.status === "success" ? "✓" : autoRefreshRun.status === "running" ? "…" : "✗"}
            </span>
          )}
        </div>
      </div>

      {/* A1 (Dean, 8 Oct 2026): Unassigned intake + caseload — Head of Legal / admins only */}
      {legalMe?.is_legal_head && <UnassignedLegalPanel stages={stages} onCasesChanged={reload} />}

      {/* Find Legal Case — LoanDisk search → escalate to Legal */}
      <FindLegalCaseDialog
        open={findOpen}
        onOpenChange={setFindOpen}
        onCaseCreated={() => reload()}
      />

      {/* Emma 7cb2874553 — court-bundle notes viewer straight from the tracker */}
      <CourtBundleDialog caseRow={bundleCase} onClose={() => setBundleCase(null)} />

      {/* Refresh All — unified progress modal (LoanDisk + Airtable phases) */}
      <Dialog open={refreshOpen} onOpenChange={(o) => { if (!refreshRunning) setRefreshOpen(o); }}>
        <DialogContent className="sm:max-w-lg" data-testid="legal-refresh-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className={`w-4 h-4 text-red-700 ${refreshRunning ? "animate-spin" : ""}`} />
              Bulk refresh — all legal data sources
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            {/* Phase 1 — LoanDisk */}
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">
                  <span className={
                    refreshPhase === "loandisk" ? "text-amber-700"
                    : (refreshPhase === "airtable" || refreshPhase === "done") ? "text-emerald-700"
                    : "text-slate-500"
                  }>
                    Phase 1: LoanDisk
                  </span>
                  {refreshPhase === "loandisk" && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                  {(refreshPhase === "airtable" || refreshPhase === "done") && <span className="text-emerald-700 text-xs">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {loanDiskState?.state || (refreshPhase === "loandisk" ? "starting…" : "pending")}
                </div>
              </div>
              {loanDiskState && (
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div><span className="text-muted-foreground">Imported:</span> <span className="font-semibold">{loanDiskState.imported || 0}</span></div>
                  <div><span className="text-muted-foreground">Updated:</span> <span className="font-semibold">{loanDiskState.updated  || 0}</span></div>
                  <div><span className="text-muted-foreground">Skipped:</span> <span className="font-semibold">{loanDiskState.skipped  || 0}</span></div>
                  {loanDiskState.total_loandisk_legal_loans != null && (
                    <div className="col-span-3 text-[10px] text-muted-foreground">
                      {loanDiskState.total_loandisk_legal_loans} legal loans found in LoanDisk
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Phase 2 — Airtable */}
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">
                  <span className={
                    refreshPhase === "airtable" ? "text-amber-700"
                    : refreshPhase === "done" ? "text-emerald-700"
                    : "text-slate-500"
                  }>
                    Phase 2: Airtable CaseNotes
                  </span>
                  {refreshPhase === "airtable" && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                  {refreshPhase === "done" && <span className="text-emerald-700 text-xs">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {bulkSyncJob?.status || (refreshPhase === "airtable" ? "starting…" : "pending")}
                </div>
              </div>
              {bulkSyncJob && (
                <>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-muted-foreground">
                      {bulkSyncJob.processed || 0} / {bulkSyncJob.total || "?"} records
                    </span>
                    <span className="tabular-nums">
                      {bulkSyncJob.total ? Math.round(100 * (bulkSyncJob.processed || 0) / bulkSyncJob.total) : 0}%
                    </span>
                  </div>
                  <Progress
                    value={bulkSyncJob.total ? (100 * (bulkSyncJob.processed || 0) / bulkSyncJob.total) : 0}
                    className="h-2 mb-2"
                  />
                  {bulkSyncJob.current_sf && bulkSyncJob.status === "running" && (
                    <p className="mb-2 text-[11px] text-muted-foreground">
                      Currently: <span className="font-mono">{bulkSyncJob.current_sf}</span>
                    </p>
                  )}
                  {bulkSyncJob.totals && (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div><span className="text-muted-foreground">Notes imported:</span> <span className="font-semibold">{bulkSyncJob.totals.imported_count || 0}</span></div>
                      <div><span className="text-muted-foreground">Deadlines:</span> <span className="font-semibold">{bulkSyncJob.totals.deadline_count || 0}</span></div>
                      <div><span className="text-muted-foreground">Calendar events:</span> <span className="font-semibold">{bulkSyncJob.totals.calendar_events_created || 0}</span></div>
                      <div><span className="text-muted-foreground">P2Ps:</span> <span className="font-semibold">{bulkSyncJob.totals.p2ps_created || 0}</span></div>
                      <div className="col-span-2"><span className="text-muted-foreground">New case records:</span> <span className="font-semibold">{bulkSyncJob.totals.cases_created || 0}</span></div>
                    </div>
                  )}
                  {bulkSyncJob.error_count > 0 && (
                    <div className="mt-2 text-[11px] text-rose-700">
                      {bulkSyncJob.error_count} record{bulkSyncJob.error_count === 1 ? "" : "s"} failed
                      ({(bulkSyncJob.errors || []).slice(-3).map(e => e.sf).join(", ")}…)
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Phase 3 — OneDrive metadata sync */}
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2">
                  <span className={
                    refreshPhase === "onedrive" ? "text-amber-700"
                    : refreshPhase === "done" ? "text-emerald-700"
                    : "text-slate-500"
                  }>
                    Phase 3: OneDrive case files
                  </span>
                  {refreshPhase === "onedrive" && <Loader2 className="w-3 h-3 animate-spin text-amber-700" />}
                  {refreshPhase === "done" && <span className="text-emerald-700 text-xs">✓</span>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {oneDriveState?.state || (refreshPhase === "onedrive" ? "starting…" : "pending")}
                </div>
              </div>
              {oneDriveState && oneDriveState.state !== "idle" && (
                <>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                    <div><span className="text-muted-foreground">Processed:</span> <span className="font-semibold">{oneDriveState.processed || 0}</span></div>
                    <div><span className="text-muted-foreground">Matched:</span> <span className="font-semibold text-emerald-700">{oneDriveState.matched || 0}</span></div>
                    <div><span className="text-muted-foreground">Unmatched:</span> <span className="font-semibold text-amber-700">{oneDriveState.unmatched || 0}</span></div>
                  </div>
                  {oneDriveState.current && oneDriveState.state === "running" && (
                    <p className="text-[10px] font-mono text-muted-foreground truncate">
                      {oneDriveState.current}
                    </p>
                  )}
                  {oneDriveState.deleted > 0 && (
                    <p className="text-[11px] text-rose-700 mt-1">
                      {oneDriveState.deleted} files removed from OneDrive since last sync
                    </p>
                  )}
                  {(oneDriveState.errors || []).length > 0 && (
                    <div className="mt-2 text-[11px] text-rose-700">
                      {(oneDriveState.errors || []).length} error{oneDriveState.errors.length === 1 ? "" : "s"} during sync
                    </div>
                  )}
                  {oneDriveState.state === "completed" && (oneDriveState.unmatched || 0) > 0 && (
                    <div className="mt-2"><UnmatchedFoldersPanel region="UK" /></div>
                  )}
                </>
              )}
            </div>

            {refreshError && (
              <div className="border border-rose-200 bg-rose-50 rounded p-2 text-xs text-rose-800">
                {refreshError}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefreshOpen(false)}
              disabled={refreshRunning}
              data-testid="legal-refresh-all-close"
            >
              {refreshRunning ? "Running…" : "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KPI tiles removed — counts now inline in the header (item 10).
          Saves vertical real-estate and matches the LoanAgeing layout. */}

      {/* Item 13: Quick filter — actions finishing within N days. Editable
          timeframe inline; default 7. */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {/* Dean, 8 Oct 2026 — quick views */}
        {myName && (
          <button
            onClick={() => setHandlerFilter(isMine ? "all" : myName)}
            className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${isMine ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
            title={isMine ? "Showing the files you own — click to clear" : "Only the files where you are the legal owner (or account handler with no legal owner)"}
            data-testid="legal-quick-mine"
          >
            <UserIcon className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold uppercase tracking-wide">My cases</span>
            {isMine && <X className="w-3 h-3 ml-0.5 opacity-70" />}
          </button>
        )}
        <button
          onClick={() => setHandlerFilter(handlerFilter === "Unassigned" ? "all" : "Unassigned")}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${handlerFilter === "Unassigned" ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-200 hover:border-slate-400 text-slate-700"}`}
          title="Files with no legal owner (the account handler doesn't count on the Legal side)"
          data-testid="legal-quick-unassigned"
        >
          <UserX className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Unassigned</span>
          <span className={`text-[11px] font-bold tabular-nums px-1 rounded border ${handlerFilter === "Unassigned" ? "bg-white/20 border-white/30" : "bg-white/70 border-slate-200"}`}>{summary.by_handler?.Unassigned || 0}</span>
        </button>
        <button
          onClick={() => setQuickView(v => v === "overdue" ? null : "overdue")}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${quickView === "overdue" ? "bg-red-100 border-red-300 text-red-900 ring-2 ring-red-200 ring-offset-1" : "bg-white border-slate-200 hover:border-red-300 text-slate-700"}`}
          title="Files whose next diary action is already past its date"
          data-testid="legal-quick-overdue"
        >
          <AlarmClock className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Overdue</span>
          <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-red-200" data-testid="legal-quick-overdue-count">{quickCounts.overdue}</span>
          {quickView === "overdue" && <X className="w-3 h-3 ml-0.5 opacity-70" />}
        </button>
        <button
          onClick={() => setQuickView(v => v === "hearings_month" ? null : "hearings_month")}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${quickView === "hearings_month" ? "bg-indigo-100 border-indigo-300 text-indigo-900 ring-2 ring-indigo-200 ring-offset-1" : "bg-white border-slate-200 hover:border-indigo-300 text-slate-700"}`}
          title="Files with a court hearing this calendar month"
          data-testid="legal-quick-hearings"
        >
          <Gavel className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Hearings this month</span>
          <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-indigo-200" data-testid="legal-quick-hearings-count">{quickCounts.hearings_month}</span>
          {quickView === "hearings_month" && <X className="w-3 h-3 ml-0.5 opacity-70" />}
        </button>
        <span className="h-5 border-l border-slate-200 mx-0.5" />
        <button
          onClick={() => {
            if (actionsWithinDays !== null) {
              setActionsWithinDays(null);
            } else {
              const days = Math.max(1, parseInt(actionsInput, 10) || 7);
              setActionsWithinDays(days);
            }
          }}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
            actionsWithinDays !== null
              ? "bg-amber-100 border-amber-300 text-amber-900 ring-2 ring-amber-200 ring-offset-1"
              : "bg-white border-slate-200 hover:border-amber-300 text-slate-700"
          }`}
          title={actionsWithinDays !== null
            ? `Showing cases with scheduled legal events in the next ${actionsWithinDays} day${actionsWithinDays === 1 ? "" : "s"} — click to clear`
            : "Filter to cases that have scheduled legal events landing within the next N days (editable below)"}
          data-testid="legal-actions-within-toggle"
        >
          <Clock className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">
            Actions finishing within
          </span>
          <input
            type="number"
            min={1}
            max={365}
            value={actionsInput}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const v = Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 1));
              setActionsInput(v);
              if (actionsWithinDays !== null) setActionsWithinDays(v);
            }}
            className="w-12 h-5 px-1 text-center rounded border border-slate-300 bg-white text-[11px] font-bold text-slate-900 tabular-nums focus:outline-none focus:border-amber-500"
            data-testid="legal-actions-within-input"
          />
          <span className="text-[11px] font-semibold">days</span>
          {actionsWithinDays !== null && (
            <X className="w-3 h-3 ml-0.5 opacity-70 hover:opacity-100" aria-label="Clear actions filter" />
          )}
        </button>

        {/* 'Review for Legal Proceedings' queue — cases auto-flagged when a DN/LBA expired */}
        <button
          onClick={() => setReviewOnly(v => !v)}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
            reviewOnly
              ? "bg-rose-100 border-rose-300 text-rose-900 ring-2 ring-rose-200 ring-offset-1"
              : "bg-white border-slate-200 hover:border-rose-300 text-slate-700"
          }`}
          title="Cases auto-flagged 'Review for Legal Proceedings' — a DN or LBA expired with no further action"
          data-testid="legal-review-queue-toggle"
        >
          <AlertTriangle className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Review for legal</span>
          <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-rose-200">
            {summary.review_count || 0}
          </span>
          {reviewOnly && <X className="w-3 h-3 ml-0.5 opacity-70 hover:opacity-100" aria-label="Clear review filter" />}
        </button>

        {/* Emma de31b7a3fd — live cases with nothing scheduled in the legal diary (risk of being lost) */}
        <button
          onClick={() => setNoNextActionOnly(v => !v)}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
            noNextActionOnly
              ? "bg-red-100 border-red-300 text-red-900 ring-2 ring-red-200 ring-offset-1"
              : summary.no_next_action > 0 ? "bg-white border-red-200 hover:border-red-400 text-red-800" : "bg-white border-slate-200 hover:border-red-300 text-slate-700"
          }`}
          title="Live legal cases with no scheduled action in the diary — nothing will prompt anyone to pick these up"
          data-testid="legal-no-next-action-toggle"
        >
          <CalendarX2 className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">No next action</span>
          <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-red-200" data-testid="legal-no-next-action-count">
            {summary.no_next_action || 0}
          </span>
          {noNextActionOnly && <X className="w-3 h-3 ml-0.5 opacity-70 hover:opacity-100" aria-label="Clear no-next-action filter" />}
        </button>

        {/* BS Protected queue — cases with an active Breathing Space (do not contact) */}
        {(summary.bs_count > 0 || bsOnly) && (
          <button
            onClick={() => setBsOnly(v => !v)}
            className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
              bsOnly
                ? "bg-purple-100 border-purple-300 text-purple-900 ring-2 ring-purple-200 ring-offset-1"
                : "bg-white border-slate-200 hover:border-purple-300 text-slate-700"
            }`}
            title="Cases with an active Breathing Space — all collections contact and enforcement must pause"
            data-testid="legal-bs-queue-toggle"
          >
            <PauseCircle className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold uppercase tracking-wide">BS Protected</span>
            <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-purple-200">
              {summary.bs_count || 0}
            </span>
            {bsOnly && <X className="w-3 h-3 ml-0.5 opacity-70 hover:opacity-100" aria-label="Clear BS filter" />}
          </button>
        )}

        {/* Dean 9 Oct 2026 — files kept on the Legal side by a legal hold ("⚖ Legal ongoing") */}
        <button
          onClick={() => setHoldOnly(v => !v)}
          className={`inline-flex items-center gap-1.5 px-2 h-7 rounded-md border transition-all ${
            holdOnly
              ? "bg-amber-100 border-amber-300 text-amber-900 ring-2 ring-amber-200 ring-offset-1"
              : "bg-white border-slate-200 hover:border-amber-300 text-slate-700"
          }`}
          title={LEGAL_HOLD_EXPLAINER}
          data-testid="legal-hold-queue-toggle"
        >
          <Scale className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold uppercase tracking-wide">Legal hold</span>
          <span className="text-[11px] font-bold tabular-nums px-1 rounded bg-white/70 border border-amber-200" data-testid="legal-hold-count">
            {summary.hold_count || 0}
          </span>
          {holdOnly && <X className="w-3 h-3 ml-0.5 opacity-70 hover:opacity-100" aria-label="Clear legal hold filter" />}
        </button>
      </div>

      {/* Filters bar (item 13: jurisdiction removed) */}
      <Card>
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by SF# / company / director…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              data-testid="legal-search"
            />
          </div>
          <Select value={handlerFilter} onValueChange={setHandlerFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="legal-filter-handler">
              <SelectValue placeholder="Handler" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All handlers</SelectItem>
              <SelectItem value="Unassigned">Unassigned</SelectItem>
              {filterHandlers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="h-9 w-[220px]" data-testid="legal-filter-stage">
              <SelectValue placeholder="Legal stage" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stages</SelectItem>
              {stagesAlpha.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={closedFilter} onValueChange={setClosedFilter}>
            <SelectTrigger className="h-9 w-[210px]" data-testid="legal-filter-closed">
              <SelectValue placeholder="Live / history" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="live">Live files only ({summary.live_count || 0})</SelectItem>
              <SelectItem value="include">Include history ({summary.history_count || 0})</SelectItem>
              <SelectItem value="only">History only ({summary.history_count || 0})</SelectItem>
            </SelectContent>
          </Select>
          {/* Emma #6 — handling + jurisdiction */}
          <Select value={handlingFilter} onValueChange={setHandlingFilter}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="legal-filter-handling">
              <SelectValue placeholder="Handling" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All handling</SelectItem>
              <SelectItem value="external">🏛 External (instructed firm)</SelectItem>
              <SelectItem value="in_house">In-house</SelectItem>
              <SelectItem value="out_of_jurisdiction">🌍 Out of jurisdiction</SelectItem>
            </SelectContent>
          </Select>
          <Select value={jurisdictionFilter} onValueChange={setJurisdictionFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="legal-filter-jurisdiction">
              <SelectValue placeholder="Jurisdiction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All jurisdictions</SelectItem>
              <SelectItem value="england_wales">England &amp; Wales</SelectItem>
              <SelectItem value="scotland">Scotland</SelectItem>
              <SelectItem value="northern_ireland">Northern Ireland</SelectItem>
            </SelectContent>
          </Select>
          {/* Emma #14 — status tags */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[180px]" data-testid="legal-filter-status">
              <SelectValue placeholder="Status tag" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status tags</SelectItem>
              {statusesAlpha.map(s => <SelectItem key={s.key} value={s.key} data-testid={`legal-filter-status-${s.key}`}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(handlerFilter !== "all" || stageFilter !== "all" || closedFilter !== "live" || handlingFilter !== "all" || jurisdictionFilter !== "all" || statusFilter !== "all" || search) && (
            <Button
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => {
                setHandlerFilter("all");
                setStageFilter("all");
                setClosedFilter("live");
                setHandlingFilter("all");
                setJurisdictionFilter("all");
                setStatusFilter("all");
                setSearch("");
              }}
              data-testid="legal-filter-reset"
            >
              Reset
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Item 12: Bulk action bar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-2.5 bg-indigo-50 border-2 border-indigo-200 rounded-md text-sm"
          data-testid="legal-bulk-bar"
        >
          <span className="font-semibold text-indigo-900">
            {selectedIds.size} selected
          </span>
          <Select disabled={bulkBusy} onValueChange={(v) => handleBulkAssign(v === "_unassign" ? null : v, "legal_handler")}>
            <SelectTrigger className="w-[220px] h-8 bg-white" data-testid="legal-bulk-assign-select">
              <SelectValue placeholder={bulkBusy ? "Assigning…" : "⚖ Set legal owner…"} />
            </SelectTrigger>
            <SelectContent>
              {handlers.map(h => (
                <SelectItem key={h} value={h} data-testid={`legal-bulk-handler-${h}`}>{h}</SelectItem>
              ))}
              <SelectItem value="_unassign" data-testid="legal-bulk-handler-unassign">— Clear legal owner —</SelectItem>
            </SelectContent>
          </Select>
          <Select disabled={bulkBusy} onValueChange={(v) => handleBulkAssign(v === "_unassign" ? null : v, "handler")}>
            <SelectTrigger className="w-[210px] h-8 bg-white" data-testid="legal-bulk-account-select">
              <SelectValue placeholder={bulkBusy ? "Assigning…" : "Account handler…"} />
            </SelectTrigger>
            <SelectContent>
              {accountHandlers.map(h => (
                <SelectItem key={h} value={h} data-testid={`legal-bulk-account-${h}`}>{h}</SelectItem>
              ))}
              <SelectItem value="_unassign" data-testid="legal-bulk-account-unassign">— Unassign —</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs ml-auto"
            onClick={() => setSelectedIds(new Set())}
            data-testid="legal-bulk-clear"
          >
            Clear selection
          </Button>
        </div>
      )}

      {/* Cases table */}
      <Card>
        <CardHeader className="py-2.5 px-4 border-b flex flex-row items-center">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {loading ? "Loading…" : `${visibleCases.length} cases`}
            {!loading && visibleCases.length !== cases.length && <span className="text-xs text-muted-foreground font-normal">of {cases.length} loaded</span>}
          </CardTitle>
          <span className="ml-auto text-[11px] text-muted-foreground" data-testid="legal-sort-label">
            Sorted by {({ customer: "customer", stage: "stage", next_action: "next action due (overdue first)", latest_note: "latest note", handler: "handler", balance: "balance" })[sort.key]} {sort.dir === "asc" ? "↑" : "↓"}
          </span>
        </CardHeader>
        <CardContent className="p-0">
          {loading && cases.length === 0 ? (
            <div className="py-12 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : visibleCases.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground text-sm">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
              No legal cases match the current filters.
              <div className="mt-2 text-xs">
                {quickView ? "Clear the quick view above to see the rest." : <>Try clicking <strong>Refresh All</strong> above to import existing legal cases.</>}
              </div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="w-10 px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleAllVisible}
                      aria-label="Select all visible cases"
                      data-testid="legal-select-all"
                    />
                  </th>
                  {[["customer", "Customer", "text-left"], ["stage", "Stage", "text-left"], ["next_action", "Next action", "text-left"],
                    ["latest_note", "Latest note", "text-left"], ["account", "Account", "text-left"], ["legal_owner", "Legal owner", "text-left"], ["balance", "Balance", "text-right"]].map(([k, label, align]) => (
                    <th key={k} className={`${align} px-4 py-2 font-semibold cursor-pointer select-none hover:text-slate-800 ${sort.key === k ? "text-slate-900" : ""}`}
                      onClick={() => toggleSort(k)} data-testid={`legal-sort-${k}`} title={`Sort by ${label.toLowerCase()}`}>
                      <span className={`inline-flex items-center gap-1 ${align === "text-right" ? "flex-row-reverse" : ""}`}>{label} <SortIcon k={k} /></span>
                    </th>
                  ))}
                  <th className="w-10 px-2"></th>
                </tr>
              </thead>
              <tbody>
                {visibleCases.map(c => {
                  const isSel = selectedIds.has(c.case_id);
                  const note = c.latest_human_note;
                  const open = () => navigate(`/legal/case/${c.case_id}?source=legal`);
                  return (
                  <tr
                    key={c.case_id}
                    className={`border-t hover:bg-slate-50 transition-colors ${isSel ? "bg-indigo-50/50" : ""}`}
                    data-testid={`legal-case-row-${c.case_id}`}
                  >
                    <td
                      className="px-3 py-2.5 cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); toggleRow(c.case_id); }}
                      data-testid={`legal-row-checkbox-${c.case_id}`}
                    >
                      <Checkbox
                        checked={isSel}
                        aria-label={`Select ${c.customer_name}`}
                        className="pointer-events-none"
                      />
                    </td>
                    <td className="px-4 py-2.5 cursor-pointer" onClick={open}>
                      <div className="font-medium text-slate-900 truncate max-w-[260px] flex items-center gap-1.5">
                        <span className="truncate">{c.customer_name || "—"}</span>
                        <span className="font-mono text-[10px] text-muted-foreground font-normal shrink-0">{c.loan_application_id || ""}</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate max-w-[260px]">{c.borrower_director_name || ""}</div>
                      <CompactBadges c={c} accountStatuses={accountStatuses} />
                    </td>
                    <td className="px-4 py-2.5 cursor-pointer" onClick={open}>
                      {c.legal_stage ? (
                        <Badge variant="outline" className="text-[11px]">{stageLabel[c.legal_stage] || humanise(c.legal_stage)}</Badge>
                      ) : (
                        <span className="text-[11px] italic text-slate-400" title="No legal stage set yet — set one on the case" data-testid={`legal-stage-unstaged-${c.case_id}`}>Not staged</span>
                      )}
                      {c.review_for_legal && (
                        <div className="mt-1">
                          <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border bg-rose-50 text-rose-700 border-rose-300"
                            title={c.review_for_legal.reason || "Flagged for review"}
                            data-testid={`legal-review-badge-${c.case_id}`}>
                            ⚠ Review for legal
                          </span>
                        </div>
                      )}
                      {c.legal_closure?.type === "fully_paid" && (
                        <div className="mt-1">
                          <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-800 border-emerald-300"
                            title={c.legal_closure.reason || "Paid in full — legal file closed"} data-testid={`legal-fullypaid-badge-${c.case_id}`}>
                            ✓ Fully paid {c.legal_closure.paid_at ? new Date(c.legal_closure.paid_at + "T00:00:00").toLocaleDateString("en-GB") : ""}
                          </span>
                        </div>
                      )}
                      {c.legal_closure?.type === "closed_review" && c.legal_closure.review_at && (
                        <div className="mt-1">
                          <span
                            className={`text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border ${
                              c.legal_closure.review_at <= new Date().toISOString().slice(0, 10)
                                ? "bg-red-50 text-red-700 border-red-300"
                                : "bg-amber-50 text-amber-800 border-amber-300"
                            }`}
                            title={c.legal_closure.reason || "Closed — diarised for a future review"}
                            data-testid={`legal-closedreview-badge-${c.case_id}`}
                          >
                            ⏰ Review {formatShortDate(c.legal_closure.review_at)}
                          </span>
                        </div>
                      )}
                      {c.legal_closure?.type === "write_off" && (
                        <div className="mt-1">
                          <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-300"
                            title={c.legal_closure.reason || "Written off — no further action will be taken"}
                            data-testid={`legal-writeoff-badge-${c.case_id}`}>
                            ✕ Final — no revisit
                          </span>
                        </div>
                      )}
                    </td>
                    {/* Traffic-light next action — earliest scheduled diary event.
                        Priority computed server-side from Portal Settings thresholds. */}
                    <td className="px-4 py-2.5 cursor-pointer" onClick={open} data-testid={`legal-next-action-${c.case_id}`}>
                      {c.next_event ? (
                        <div className="flex items-center gap-1.5">
                          <PriorityDot priority={c.next_event.priority} daysOverdue={c.next_event.days_overdue} />
                          <div className="min-w-0">
                            <div className="text-[11px] font-medium text-slate-800 truncate max-w-[190px]" title={c.next_event.label}>{c.next_event.label}</div>
                            <div className={`text-[10px] tabular-nums ${c.next_event.priority === "red" ? "text-red-700 font-semibold" : c.next_event.priority === "amber" ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>
                              {formatShortDate(c.next_event.date)}
                              {c.next_event.days_overdue > 0 && ` · ${c.next_event.days_overdue}d overdue`}
                            </div>
                          </div>
                        </div>
                      ) : <span className="text-[11px] italic text-red-700/80" title="Nothing diarised — this file will not prompt anyone">No next action</span>}
                      {c.hearing && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-800 border-indigo-200"
                          title={`Court hearing ${formatShortDate(c.hearing.date)} — ${c.hearing.prep_done}/${c.hearing.prep_total} prep tasks complete`}
                          data-testid={`legal-hearing-chip-${c.case_id}`}>
                          <Landmark className="w-2.5 h-2.5" />
                          {formatShortDate(c.hearing.date)}
                          {c.hearing.prep_total > 0 && (
                            <span className={c.hearing.prep_done < c.hearing.prep_total ? "text-amber-700" : "text-emerald-700"}>
                              · prep {c.hearing.prep_done}/{c.hearing.prep_total}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    {/* Latest HUMAN note (Dean, 8 Oct 2026) — system lines never show here; full text on hover */}
                    <td className="px-4 py-2.5 text-[11px] max-w-[300px] cursor-pointer" onClick={open} data-testid={`legal-latest-note-${c.case_id}`}>
                      {note ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="min-w-0">
                              <div className="truncate text-slate-700">{truncate(note.text, 70)}</div>
                              <div className="text-[10px] text-muted-foreground truncate">{noteKindLabel(note.type)}{note.author ? ` · ${shortAuthor(note.author)}` : ""}{note.at ? ` · ${formatShortDate(note.at)}` : ""}</div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-md text-xs whitespace-pre-wrap" data-testid={`legal-note-tooltip-${c.case_id}`}>
                            {note.text}
                          </TooltipContent>
                        </Tooltip>
                      ) : <span className="italic text-slate-400">No notes yet</span>}
                    </td>
                    {/* Dean, 9 Oct 2026 — account handler and legal owner side by side */}
                    <td className="px-4 py-2.5 cursor-pointer text-xs" onClick={open} data-testid={`legal-account-handler-${c.case_id}`} title="Account handler (Collections)">
                      {c.handler || <span className="text-muted-foreground italic">Unassigned</span>}
                    </td>
                    <td className="px-4 py-2.5 cursor-pointer text-xs" onClick={open} data-testid={`legal-owner-${c.case_id}`} title={c.legal_handler ? "Legal owner — owns this file's legal actions" : "No separate legal owner — legal actions follow the account handler"}>
                      {c.legal_handler ? <span className="text-red-800">⚖ {c.legal_handler}</span>
                        : c.handler ? <span className="text-muted-foreground">= {c.handler.split(" ")[0]}</span>
                        : <span className="text-muted-foreground italic">Unassigned</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium tabular-nums cursor-pointer" onClick={open}>
                      {fmtGBP(c.balance_amount)}
                    </td>
                    <td className="px-2 py-2.5 text-muted-foreground" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button type="button" className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-slate-100" data-testid={`legal-row-menu-${c.case_id}`} title="More">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="text-xs">
                          <DropdownMenuItem onClick={open} data-testid={`legal-row-open-${c.case_id}`}><ArrowRight className="w-3.5 h-3.5 mr-2" /> Open case</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/legal/case/${c.case_id}?source=legal&tab=p2ps`)} data-testid={`legal-row-schedule-${c.case_id}`}><CalIcon className="w-3.5 h-3.5 mr-2" /> Legal schedule</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setBundleCase(c)} data-testid={`legal-court-bundle-btn-${c.case_id}`}><FileText className="w-3.5 h-3.5 mr-2" /> Court bundle (full notes)</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
}

// Dean, 9 Oct 2026 — "⚖ Legal ongoing" was unclear: the hover now explains the legal hold in plain words + who set it and why
const LEGAL_HOLD_EXPLAINER = "Legal hold — the account has left the Legal stage (e.g. restructured back to an open loan or de-escalated) but the legal action is still live, so the file stays on the Legal side. Joint ownership: Collections keeps the account handler, the legal owner keeps the legal work. Clear it from the Customer File header once the legal work is wrapped up.";
const fmtWhen = (iso) => { if (!iso) return ""; const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB"); };
function legalHoldTitle(hold) {
  const who = hold.set_by ? ` Set by ${hold.set_by}${hold.set_at ? ` on ${fmtWhen(hold.set_at)}` : ""}${hold.auto ? " (automatically — a legal action was still diarised when the stage changed)" : ""}.` : "";
  return `⚖ LEGAL ONGOING = legal hold.\n${LEGAL_HOLD_EXPLAINER}\n\nReason: ${hold.reason || "not recorded"}.${who}`;
}
function LegalHoldHover({ hold, caseId }) {
  return (
    <div className="space-y-1.5 max-w-xs" data-testid={`legal-hold-hover-${caseId}`}>
      <div className="font-semibold">⚖ Legal ongoing = legal hold</div>
      <p className="leading-snug">{LEGAL_HOLD_EXPLAINER}</p>
      <p className="leading-snug"><span className="font-semibold">Reason:</span> {hold.reason || "not recorded"}</p>
      {hold.set_by && <p className="leading-snug text-slate-300">Set by {hold.set_by}{hold.set_at ? ` on ${fmtWhen(hold.set_at)}` : ""}{hold.auto ? " · automatic (a legal action was still diarised when the stage changed)" : ""}</p>}
    </div>
  );
}

// Dean, 8 Oct 2026 — the badge pile under the customer name becomes one compact row: first two chips + "+N" (hover lists the rest)
function CompactBadges({ c, accountStatuses }) {
  const chips = [];
  // Dean, 9 Oct 2026 — status banner first: "Legal" · "Open loan · Legal ongoing" · "Legal · LD says Open — tidy up" (+ history labels)
  const ls = c.legal_status;
  if (ls) {
    const tone = ls.key === "legal" ? "bg-rose-50 text-rose-800 border-rose-300"
      : ls.key === "legal_ld_open" ? "bg-orange-50 text-orange-800 border-orange-400"
      : ls.key === "legal_ongoing" ? "bg-amber-50 text-amber-800 border-amber-300"
      : "bg-slate-100 text-slate-600 border-slate-300";
    const hover = ls.key === "legal_ongoing" && c.legal_hold?.active ? <LegalHoldHover hold={c.legal_hold} caseId={c.case_id} />
      : ls.flag ? <div className="max-w-xs space-y-1"><div className="font-semibold">{ls.label}</div><p className="leading-snug">{ls.flag}</p></div>
      : null;
    chips.push({ key: "live", label: ls.label, tone, title: ls.flag || (ls.key === "legal_ongoing" ? LEGAL_HOLD_EXPLAINER : ls.label), hover, testid: `legal-live-badge-${c.case_id}` });
  } else if (c.legal_hold?.active) {
    chips.push({ key: "hold", label: "⚖ Legal ongoing", tone: "bg-amber-50 text-amber-800 border-amber-300", title: legalHoldTitle(c.legal_hold), hover: <LegalHoldHover hold={c.legal_hold} caseId={c.case_id} />, testid: `legal-hold-badge-${c.case_id}` });
  }
  if (c.handling_marker === "out_of_jurisdiction") chips.push({ key: "ooj", label: "🌍 Out of jurisdiction", tone: "bg-sky-50 text-sky-800 border-sky-300", title: "Out of jurisdiction (Scottish / NI) — limited actions", testid: `legal-ooj-badge-${c.case_id}` });
  if (c.handling_marker === "external") chips.push({ key: "ext", label: `🏛 External${c.external_firm ? ` · ${c.external_firm}` : ""}`, tone: "bg-indigo-50 text-indigo-800 border-indigo-300", title: c.external_firm ? `Handled externally by ${c.external_firm}` : "Handled externally", testid: `legal-external-badge-${c.case_id}` });
  if (c.breathing_space?.active) chips.push({ key: "bs", label: `⏸ BS Protected${c.breathing_space.active_count > 1 ? ` ×${c.breathing_space.active_count}` : ""}`, tone: "bg-purple-50 text-purple-700 border-purple-300", title: `Breathing Space active — do not contact${c.breathing_space.end_date ? ` · ends ${c.breathing_space.end_date}` : ""}`, testid: `legal-bs-badge-${c.case_id}` });
  if ((c.pg_insolvencies || []).length) chips.push({ key: "pg", label: "⚠ PG insolvency", tone: "bg-rose-50 text-rose-800 border-rose-300", title: "A guarantor is bankrupt / in an IVA — enforcement against them is blocked", testid: `legal-pg-badge-${c.case_id}` });
  for (const k of c.manual_statuses || []) {
    const s = (accountStatuses || []).find(x => x.key === k);
    chips.push({ key: `st-${k}`, label: s?.label || k, tone: "bg-slate-50 text-slate-700 border-slate-300", title: s?.label || k, testid: `legal-status-tag-${c.case_id}-${k}` });
  }
  if (!chips.length) return null;
  const shown = chips.slice(0, 2), rest = chips.slice(2);
  return (
    <div className="flex items-center gap-1 mt-1 flex-nowrap min-w-0" data-testid={`legal-badges-${c.case_id}`}>
      {shown.map(ch => ch.hover ? (
        <Tooltip key={ch.key}>
          <TooltipTrigger asChild>
            <span className={`inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap truncate max-w-[150px] cursor-help ${ch.tone}`} data-testid={ch.testid}>{ch.label}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" align="start" className="text-xs">{ch.hover}</TooltipContent>
        </Tooltip>
      ) : (
        <span key={ch.key} className={`inline-flex items-center text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border whitespace-nowrap truncate max-w-[150px] ${ch.tone}`} title={ch.title} data-testid={ch.testid}>{ch.label}</span>
      ))}
      {rest.length > 0 && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center text-[9px] font-semibold px-1.5 py-0.5 rounded border bg-white text-slate-600 border-slate-300 cursor-default" data-testid={`legal-badges-more-${c.case_id}`}>+{rest.length}</span>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="text-xs"><div className="space-y-0.5">{rest.map(ch => <div key={ch.key}>{ch.label}</div>)}</div></TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

const humanise = (key) => (key || "").replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());
const noteKindLabel = (t) => ({ call: "Call", sms: "SMS", email: "Email", note: "Note", complaint: "Complaint", legal_note: "Legal note" })[t] || "Note";
const shortAuthor = (a) => {
  const s = String(a || "");
  if (s.startsWith("Airtable")) return "Airtable";
  if (s.includes("@")) return s.split("@")[0];
  const parts = s.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}.` : s;
};

// Count cases that are in an active legal proceeding (between DN/LBA and
// pre-resolution). Excludes resolved buckets (write_off, fully_paid,
// payment_plan, collections_restructured) + un-staged.
function countActiveStages(byStage = {}) {
  const RESOLVED = new Set(["write_off", "fully_paid", "payment_plan", "collections_restructured", "_unstaged"]);
  return Object.entries(byStage).reduce(
    (acc, [k, v]) => acc + (RESOLVED.has(k) ? 0 : v),
    0,
  );
}

// "2026-07-04" → "4 Jul" (compact for the Next-action column)
function formatShortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso.length > 10 ? iso : iso + "T00:00:00");
  return isNaN(d) ? iso : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// Emma 7cb2874553 — full case notes + raw Airtable text (court-bundle view)
// accessible straight from the tracker, mirroring the ageing account page.
function CourtBundleDialog({ caseRow, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  useEffect(() => {
    if (!caseRow) return;
    setData(null); setErr(null); setShowRaw(false); setLoading(true);
    getLegalNotesBundle(caseRow.case_id)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [caseRow]);

  const entries = data?.timeline || [];
  const blob = (data?.case_notes || "").trim();

  const copyBlob = async () => {
    try {
      await navigator.clipboard.writeText(blob);
      toast.success("Raw notes copied — ready for the court bundle");
    } catch {
      toast.error("Couldn't copy — select the text manually");
    }
  };

  return (
    <Dialog open={!!caseRow} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="legal-court-bundle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-700" />
            Case notes — {caseRow?.customer_name || caseRow?.case_id}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading notes…
          </div>
        ) : err ? (
          <div className="text-sm text-rose-700 py-4" data-testid="court-bundle-error">{err}</div>
        ) : (
          <div className="space-y-3">
            {entries.length === 0 && !blob && (
              <div className="text-sm italic text-slate-400 py-4">No notes recorded for this case yet.</div>
            )}
            {entries.length > 0 && (
              <ul className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="court-bundle-entries">
                {[...entries].sort((a, b) => (b.date_added || "").localeCompare(a.date_added || "")).map((e, idx) => (
                  <li key={e.id || idx} className="border rounded-md px-2.5 py-1.5 bg-white">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline" className="text-[9px]">{(e.entry_type || "note").replace(/_/g, " ")}</Badge>
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {e.date_added || ""}{e.effective_date ? ` · ➜ ${e.effective_date}` : ""}
                      </span>
                    </div>
                    {e.notes && <p className="text-[12px] text-slate-700 whitespace-pre-wrap leading-snug mt-1">{e.notes}</p>}
                  </li>
                ))}
              </ul>
            )}
            {blob && (
              <div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowRaw(v => !v)}
                    className="text-[11px] text-amber-700 hover:underline"
                    data-testid="court-bundle-toggle-raw"
                  >
                    {showRaw ? "Hide raw text" : "Show raw Airtable text (court-bundle view)"}
                  </button>
                  {showRaw && (
                    <button type="button" onClick={copyBlob} className="text-[11px] text-blue-700 hover:underline" data-testid="court-bundle-copy-raw">
                      Copy raw text
                    </button>
                  )}
                </div>
                {showRaw && (
                  <div
                    data-testid="court-bundle-raw-blob"
                    className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200 max-h-72 overflow-y-auto whitespace-pre-wrap leading-relaxed text-[12px]"
                  >
                    {blob}
                  </div>
                )}
              </div>
            )}
            {data?.last_synced_at && (
              <div className="text-[10px] text-muted-foreground text-right">
                Last Airtable sync: {new Date(data.last_synced_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
